import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { categorie as apiCategorie, scadenze as apiScadenze } from '../api/scadenziario.ts';
import type { Categoria, Elenco, Scadenza, Situazione } from '../api/tipi-dati.ts';
import { BadgeSituazione, classeRiga } from '../componenti/BadgeSituazione.tsx';
import { DialogoAnnullamento } from '../componenti/DialogoAnnullamento.tsx';
import { DialogoCompletamento } from '../componenti/DialogoCompletamento.tsx';
import { DialogoDettaglio } from '../componenti/DialogoDettaglio.tsx';
import { DialogoRiapertura } from '../componenti/DialogoRiapertura.tsx';
import { DialogoScadenza } from '../componenti/DialogoScadenza.tsx';
import {
  DialogoEliminazione,
  type BersaglioEliminazione,
} from '../componenti/DialogoEliminazione.tsx';
import { AzioniScadenza } from '../componenti/AzioniScadenza.tsx';
import { IconaCampanaBarrata } from '../componenti/Icona.tsx';
import { SegnoEliminato } from '../componenti/SegnoEliminato.tsx';
import { dataBreve, giorniConSegno } from '../componenti/formato.ts';
import { useAutenticazione } from '../auth/contesto.tsx';
import { IntestazioneOrdinabile, useOrdinamento } from '../componenti/IntestazioneOrdinabile.tsx';
import { ordinaRighe, type Valore } from '../componenti/ordinamento.ts';
import { puoRedigere } from '../auth/ruoli.ts';

const SITUAZIONI: { valore: Situazione; etichetta: string }[] = [
  { valore: 'SCADUTA', etichetta: 'Scadute' },
  { valore: 'IN_SCADENZA', etichetta: 'In scadenza' },
  { valore: 'PIANIFICATA', etichetta: 'Pianificate' },
  { valore: 'COMPLETATA', etichetta: 'Completate' },
  { valore: 'ANNULLATA', etichetta: 'Annullate' },
];

/**
 * Il valore del filtro «categoria» che vale «nessuna».
 *
 * Una parola e non un numero apposta: le altre voci del menu sono id di
 * categorie vere, e un sentinella numerico (`-1`, `0`) prima o poi finirebbe
 * per essere letto come uno di quelli. Così l'indirizzo si legge anche a
 * occhio — `?categoria=nessuna` — e resta condivisibile come tutti gli altri
 * filtri.
 */
const SENZA_CATEGORIA = 'nessuna';


/**
 * ⚠️ **La situazione non si ordina alfabeticamente ma per urgenza.** In ordine
 * alfabetico verrebbe «In scadenza, Pianificata, Scaduta»: tre parole in fila
 * che non dicono niente. In salita le più urgenti per prime, come già fanno la
 * data e i giorni — così le tre colonne che parlano di tempo si comportano
 * tutte allo stesso modo.
 */
const URGENZA: Record<Situazione, number> = {
  SCADUTA: 0,
  IN_SCADENZA: 1,
  PIANIFICATA: 2,
  COMPLETATA: 3,
  ANNULLATA: 4,
};

/**
 * Quanto vale una cella dell'elenco per l'ordinamento.
 *
 * ⚠️ La descrizione si ordina per **quello che si vede**, ripiego compreso: la
 * colonna mostra il nome del tipo quando la descrizione manca, e ordinare sul
 * campo grezzo manderebbe in fondo — fra le celle vuote — righe che a schermo
 * hanno del testo scritto.
 */
function valoreScadenza(s: Scadenza, chiave: string): Valore {
  switch (chiave) {
    case 'data': return s.dataScadenza;
    case 'soggetto': return s.soggetto.denominazione;
    case 'descrizione': return s.descrizione ?? s.tipo.nome;
    case 'situazione': return URGENZA[s.situazione];
    case 'giorni': return s.giorniMancanti;
    default: return null;
  }
}

export function ElencoScadenze() {
  const { ordinamento, ordina } = useOrdinamento();
  const { utente } = useAutenticazione();
  const puoEliminare = puoRedigere(utente?.ruolo);

  // I filtri vivono nell'URL, non nello stato del componente: così una
  // ricerca si può mettere fra i preferiti, mandare a un collega, e il tasto
  // "indietro" del browser fa quello che l'utente si aspetta.
  const [parametri, setParametri] = useSearchParams();

  const [dati, setDati] = useState<Elenco<Scadenza> | null>(null);
  const [categorie, setCategorie] = useState<Categoria[]>([]);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCaricamento, setInCaricamento] = useState(true);

  // Quale dialogo è aperto e su quale scadenza.
  const [daCompletare, setDaCompletare] = useState<Scadenza | null>(null);
  const [daAnnullare, setDaAnnullare] = useState<Scadenza | null>(null);
  const [daRiaprire, setDaRiaprire] = useState<Scadenza | null>(null);
  /** `undefined` = chiuso · `null` = nuova · numero = modifica di quella scadenza. */
  const [formAperto, setFormAperto] = useState<number | null | undefined>(undefined);
  /** La scadenza di cui si sta guardando la scheda in sola lettura. */
  const [daGuardare, setDaGuardare] = useState<Scadenza | null>(null);
  /** La scadenza per cui è aperta la conferma di cancellazione definitiva. */
  const [daEliminare, setDaEliminare] = useState<BersaglioEliminazione | null>(null);
  const [conferma, setConferma] = useState<string | null>(null);
  const [ricarica, setRicarica] = useState(0);

  const situazione = parametri.get('situazione') ?? '';
  const categoria = parametri.get('categoria') ?? '';
  const cerca = parametri.get('cerca') ?? '';
  const dal = parametri.get('dal') ?? '';
  const al = parametri.get('al') ?? '';

  const conFiltri = [situazione, categoria, cerca, dal, al].some((v) => v !== '');

  // Le categorie per il filtro: si leggono una volta sola, non a ogni
  // cambio di filtro.
  useEffect(() => {
    let annullato = false;

    apiCategorie
      .elenca({ attiva: 'true', limite: 200 })
      .then((r) => { if (!annullato) setCategorie(r.elementi); })
      .catch(() => undefined);

    return () => { annullato = true; };
  }, []);

  useEffect(() => {
    let annullato = false;
    setInCaricamento(true);

    apiScadenze
      .elenca({
        ...(situazione !== '' ? { situazione: situazione as Situazione } : {}),
        // Le due voci del filtro sono alternative, non cumulabili: o si chiede
        // una categoria precisa, o si chiedono quelle che non ne hanno.
        ...(categoria === SENZA_CATEGORIA
          ? { senzaCategoria: 'true' as const }
          : categoria !== ''
            ? { categoriaId: Number(categoria) }
            : {}),
        ...(cerca !== '' ? { cerca } : {}),
        ...(dal !== '' ? { dal } : {}),
        ...(al !== '' ? { al } : {}),
      })
      .then((r) => { if (!annullato) { setDati(r); setErrore(null); } })
      .catch((e: unknown) => {
        if (!annullato) setErrore(e instanceof Error ? e.message : 'Errore imprevisto');
      })
      .finally(() => { if (!annullato) setInCaricamento(false); });

    return () => { annullato = true; };
  }, [situazione, categoria, cerca, dal, al, ricarica]);

  /**
   * Dopo un'azione andata a buon fine: si chiude il dialogo, si ricarica
   * l'elenco e si conferma cosa è successo. La conferma conta soprattutto per
   * il completamento, dove l'operazione crea anche una scadenza futura.
   */
  const dopoAzione = useCallback((messaggio: string) => {
    setDaCompletare(null);
    setDaAnnullare(null);
    setDaRiaprire(null);
    setFormAperto(undefined);
    setDaEliminare(null);
    setDaGuardare(null);
    setConferma(messaggio);
    setRicarica((n) => n + 1);
  }, []);

  /**
   * Il nome del soggetto è quello che il dialogo chiederà di ricopiare.
   *
   * Non il tipo e non la data: in un elenco è la colonna che l'occhio segue, e
   * «sto cancellando la riga di Rossi o quella di Bianchi?» è lo scambio
   * probabile. La riga per esteso — tipo, soggetto e data — resta comunque
   * scritta in cima al dialogo, presa dal backend.
   */
  const chiediEliminazione = useCallback((id: number, nomeSoggetto: string) => {
    setFormAperto(undefined);
    setDaEliminare({
      entita: 'scadenza',
      id,
      nome: nomeSoggetto,
      etichettaNome: 'il nome del soggetto',
    });
  }, []);

  function aggiorna(chiave: string, valore: string) {
    const nuovi = new URLSearchParams(parametri);
    if (valore === '') nuovi.delete(chiave);
    else nuovi.set(chiave, valore);
    setParametri(nuovi, { replace: true });
  }

  return (
    <>
      <div className="titolo-pagina">
        <h1>Elenco scadenze</h1>
        {dati !== null && (
          <span className="conteggio">
            {dati.elementi.length} righe su {dati.totale}
          </span>
        )}
        <button
          type="button"
          className="bottone bottone-principale azione-titolo"
          onClick={() => setFormAperto(null)}
        >
          <span aria-hidden="true">+</span> Nuova scadenza
        </button>
      </div>

      <p className="sottotitolo-pagina">
        Una scadenza è il fatto singolo: questo soggetto, questo tipo, questa data. Sono
        ordinate per data crescente, così le già scadute e le più vicine stanno in cima. I
        filtri restano nell'indirizzo: una ricerca si può salvare fra i preferiti o mandare a
        un collega.
      </p>

      {conferma !== null && (
        <div className="avviso-esito" role="status">
          {conferma}
          <button type="button" className="bottone-piatto" onClick={() => setConferma(null)}>
            Chiudi
          </button>
        </div>
      )}

      {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}

      <div className="filtri">
        <div className="campo">
          <label htmlFor="f-situazione">Situazione</label>
          <select
            id="f-situazione"
            value={situazione}
            onChange={(e) => aggiorna('situazione', e.target.value)}
          >
            <option value="">Tutte le situazioni</option>
            {SITUAZIONI.map((s) => (
              <option key={s.valore} value={s.valore}>{s.etichetta}</option>
            ))}
          </select>
        </div>

        <div className="campo">
          <label htmlFor="f-categoria">Categoria</label>
          <select
            id="f-categoria"
            value={categoria}
            onChange={(e) => aggiorna('categoria', e.target.value)}
          >
            <option value="">Tutte le categorie</option>
            {/* Dall'API, non da un elenco scritto qui: le categorie le crea
                l'ufficio (3b). Solo le attive: filtrare per una categoria
                dismessa è una ricerca che non serve più a nessuno. */}
            {categorie.map((c) => (
              <option key={c.id} value={c.id}>{c.nome}</option>
            ))}
            {/*
              Le righe che nessuna categoria rivendica più.

              Eliminare una categoria non cancella i tipi che la usavano: li
              lascia in piedi senza categoria (ON DELETE SET NULL). Le loro
              scadenze restano nell'elenco ma smettono di rispondere a
              qualsiasi voce qui sopra, e senza questa riga l'unico modo di
              ritrovarle sarebbe scorrere tutto. Vale anche per le scadenze
              rimaste senza tipo.
            */}
            <option value={SENZA_CATEGORIA}>Senza categoria</option>
          </select>
        </div>

        <div className="campo">
          <label htmlFor="f-cerca">Soggetto</label>
          <input
            id="f-cerca"
            type="text"
            placeholder="dipendente o attrezzatura"
            defaultValue={cerca}
            // Si aggiorna quando si esce dal campo o si preme Invio, non a
            // ogni tasto: una richiesta per lettera digitata è sprecata.
            onBlur={(e) => aggiorna('cerca', e.target.value.trim())}
            onKeyDown={(e) => {
              if (e.key === 'Enter') aggiorna('cerca', e.currentTarget.value.trim());
            }}
          />
        </div>

        <div className="campo">
          <label htmlFor="f-dal">Dal</label>
          <input id="f-dal" type="date" value={dal} onChange={(e) => aggiorna('dal', e.target.value)} />
        </div>

        <div className="campo">
          <label htmlFor="f-al">Al</label>
          <input id="f-al" type="date" value={al} onChange={(e) => aggiorna('al', e.target.value)} />
        </div>

        {conFiltri && (
          <button type="button" className="bottone-piatto" onClick={() => setParametri({}, { replace: true })}>
            Azzera filtri
          </button>
        )}
      </div>

      <div className="pannello">
        {inCaricamento ? (
          <p className="stato-caricamento">Caricamento…</p>
        ) : dati === null || dati.elementi.length === 0 ? (
          <p className="stato-vuoto">
            {conFiltri
              ? 'Nessuna scadenza corrisponde ai filtri impostati.'
              : 'Non ci sono ancora scadenze registrate.'}
          </p>
        ) : (
          <div className="tabella-contenitore">
            <table className="tabella-centrata">
              <caption className="solo-lettori-schermo">
                Elenco delle scadenze, ordinate per data crescente
              </caption>
              <thead>
                <tr>
                  <IntestazioneOrdinabile chiave="data" ordinamento={ordinamento} onOrdina={ordina}>Data</IntestazioneOrdinabile>
                  <IntestazioneOrdinabile chiave="soggetto" ordinamento={ordinamento} onOrdina={ordina}>Soggetto</IntestazioneOrdinabile>
                  <IntestazioneOrdinabile chiave="descrizione" ordinamento={ordinamento} onOrdina={ordina}>Descrizione</IntestazioneOrdinabile>
                  {/* Colonna della campana: senza titolo visibile — sarebbe
                      una parola per una casella quasi sempre vuota — ma con
                      un nome per i lettori di schermo, che altrimenti si
                      troverebbero una colonna anonima. */}
                  <th scope="col" className="col-avvisi">
                    <span className="solo-lettori-schermo">Avvisi</span>
                  </th>
                  <IntestazioneOrdinabile chiave="situazione" ordinamento={ordinamento} onOrdina={ordina}>Situazione</IntestazioneOrdinabile>
                  <IntestazioneOrdinabile chiave="giorni" ordinamento={ordinamento} onOrdina={ordina}>Giorni</IntestazioneOrdinabile>
                  {/* Colonna dell'esito: senza titolo visibile, ma un nome per
                      i lettori di schermo — una colonna anonima li lascerebbe
                      senza appiglio per capire cosa contiene. */}
                  <th scope="col">
                    <span className="solo-lettori-schermo">Esito</span>
                  </th>
                  <th scope="col" className="col-azione">Azione</th>
                </tr>
              </thead>
              <tbody>
                {ordinaRighe(dati.elementi, ordinamento, valoreScadenza).map((s) => (
                  <tr
                    key={s.id}
                    className={`${classeRiga(s.situazione)} riga-cliccabile`}
                    onClick={(e) => {
                      // La colonna delle azioni ha già i suoi comandi: un clic
                      // là dentro non deve aprire anche la scheda.
                      if ((e.target as HTMLElement).closest('.col-azione') !== null) return;
                      setDaGuardare(s);
                    }}
                  >
                    <td className="data">{dataBreve(s.dataScadenza)}</td>
                    <td>
                      {/* Il nome è lo stesso che si vedeva prima — vivo o
                          congelato non si distinguono a leggerli — quindi la
                          differenza la fa il segno accanto, non il testo. */}
                      {/*
                        Un pulsante vero, e non solo la riga cliccabile: la
                        riga si raggiunge col mouse, questo anche con Tab e
                        Invio, ed è ciò che rende la scheda apribile da chi
                        naviga da tastiera o con un lettore di schermo.
                      */}
                      <button
                        type="button"
                        className="soggetto-nome bottone-testo"
                        onClick={(e) => { e.stopPropagation(); setDaGuardare(s); }}
                      >
                        {s.soggetto.denominazione}
                      </button>
                      {s.soggetto.eliminato && <SegnoEliminato cosa="soggetto" />}
                    </td>
                    <td className="tipo-nome col-descrizione">
                      {/*
                        ⚠️ Dal 27/8/2026 qui c'è la **descrizione**, non più il
                        nome del tipo: è quello con cui Enrico riconosce la riga.
                        Il nome del tipo resta nella scheda di dettaglio.

                        Il ripiego sul nome del tipo non è decorazione. La
                        descrizione è obbligatoria solo da oggi, e le scadenze
                        inserite prima possono non averla: senza ripiego quelle
                        righe sarebbero **vuote**. Sulle scadenze il cui tipo è
                        stato eliminato l'etichetta congelata resta l'unica cosa
                        che dice di che cosa si trattava (migrazione 0006).
                      */}
                      {s.descrizione ?? s.tipo.nome}
                      {s.tipo.eliminato && <SegnoEliminato cosa="tipo" />}
                    </td>
                    <td className="col-avvisi">
                      {/*
                        Solo quando manca qualcuno da avvisare. Una campana
                        accesa su ogni riga normale sarebbe rumore: la colonna
                        deve saltare all'occhio proprio perché è quasi sempre
                        vuota.
                      */}
                      {s.muta === true && (
                        <span
                          className="segno-muta"
                          title="Nessuno verrà avvisato di questa scadenza"
                        >
                          <IconaCampanaBarrata lato={15} />
                          <span className="solo-lettori-schermo">
                            Nessuno verrà avvisato di questa scadenza
                          </span>
                        </span>
                      )}
                    </td>
                    <td><BadgeSituazione situazione={s.situazione} /></td>
                    <td className={`numero${s.giorniMancanti < 0 && s.stato === 'APERTA' ? ' negativo' : ''}`}>
                      {s.stato === 'APERTA' ? giorniConSegno(s.giorniMancanti) : '—'}
                    </td>

                    <td className="esito">
                      {s.stato === 'COMPLETATA' && s.dataCompletamento !== null
                        ? `Completata il ${dataBreve(s.dataCompletamento)}`
                        : s.stato === 'ANNULLATA'
                          ? `Annullata — ${s.motivoAnnullamento ?? 'senza motivo indicato'}`
                          : ''}
                    </td>

                    <td className="col-azione">
                      <AzioniScadenza
                        scadenza={s}
                        puoEliminare={puoEliminare}
                        onModifica={() => setFormAperto(s.id)}
                        onCompleta={() => setDaCompletare(s)}
                        onAnnulla={() => setDaAnnullare(s)}
                        onRiapri={() => setDaRiaprire(s)}
                        onElimina={() => chiediEliminazione(s.id, s.soggetto.denominazione)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {daCompletare !== null && (
        <DialogoCompletamento
          scadenza={daCompletare}
          onChiudi={() => setDaCompletare(null)}
          onCompletata={dopoAzione}
        />
      )}

      {daAnnullare !== null && (
        <DialogoAnnullamento
          scadenza={daAnnullare}
          onChiudi={() => setDaAnnullare(null)}
          onAnnullata={dopoAzione}
        />
      )}

      {daRiaprire !== null && (
        <DialogoRiapertura
          scadenza={daRiaprire}
          onChiudi={() => setDaRiaprire(null)}
          onRiaperta={dopoAzione}
        />
      )}

      {formAperto !== undefined && (
        <DialogoScadenza
          scadenzaId={formAperto}
          puoEliminare={puoEliminare}
          onChiudi={() => setFormAperto(undefined)}
          onSalvata={dopoAzione}
          onElimina={chiediEliminazione}
        />
      )}

      {daGuardare !== null && (
        <DialogoDettaglio
          scadenza={daGuardare}
          onChiudi={() => setDaGuardare(null)}
          onModifica={() => { const id = daGuardare.id; setDaGuardare(null); setFormAperto(id); }}
        />
      )}

      {daEliminare !== null && (
        <DialogoEliminazione
          bersaglio={daEliminare}
          onChiudi={() => setDaEliminare(null)}
          onEliminato={dopoAzione}
        />
      )}
    </>
  );
}
